import { useStable } from '@synqo/frontend-component-library';
import { useCallback, useEffect, useState } from 'react';
import { matchesLocality, onlyLocality } from './cityMatching';
import { type LocalityLookup } from './useLocalitiesForPostalCode';

// @ai-generated 2026-09-21T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

export interface UseCityDecisionArgs {
    lookup: LocalityLookup;
    /** The address being edited — another one is a clean slate. */
    addressId: string;
    /** The city in the record. */
    city: string;
    applyCity: (city: string) => void;
    /**
     * False where the city may not be written at all. Nothing is then written or cleared either —
     * the field is somebody else's, and the postal code has no say over it.
     */
    enabled: boolean;
    /**
     * True while the frame is in a write mode. Dropping back out of one is the save — or the
     * discard, which amounts to the same thing here — and the automation starts over there.
     */
    isEditing: boolean;
}

export interface CityDecisionState {
    /**
     * A city written and not yet visible in the record. Until it is, the city on screen is the one
     * being replaced, and everything derived from the field should read this instead.
     */
    pendingCity: string | undefined;

    /**
     * True while the city in the record is one the automation put there, an empty field included.
     * The one thing it may write over — and the one thing it never disputes, because a city of its
     * own making is not evidence of anything.
     */
    isCityOurs: boolean;

    /**
     * What the answer at hand makes of the city field: the locality it settles on, nothing where it
     * leaves a choice, and whatever is already there where the field is not the automation's to
     * settle. What the field is going to show, in other words — which is what everything said about
     * the city and done to the cursor follows, rather than the record they are a step ahead of.
     */
    cityWhenSettled: string;

    /** The city the user kept against the postal code, if they have. */
    keptCity: string | undefined;

    /** True once the user has typed in the postal code field, which is the only way to ask. */
    hasTypedZip: boolean;

    /** True once the user has typed in either field — until then there is nothing to dispute. */
    hasEdited: boolean;

    /** The postal code field reports what it now shows. */
    noteZipTyped: () => void;

    /** Take one of the localities as the city and write it — for callers with no field to commit. */
    acceptLocality: (locality: string) => void;

    /** A locality the user picked in the city field's suggestion list, which commits it itself. */
    noteLocalityPicked: (locality: string) => void;

    /** Keep the city in the record and settle its contradiction with the postal code. */
    keepCity: () => void;

    /**
     * The city field reports what it now shows. Anything other than what the record holds is the
     * user taking the field in hand, and the automation then keeps off it.
     */
    noteCityTyping: (value: string) => void;

    /** The city field reports that the user has left it again. */
    noteCityBlurred: () => void;
}

/**
 * Writing side of the postal code automation, and the whole of its idea of what it may touch.
 *
 * It writes only into an empty field or over a city it put there itself, and only in answer to a
 * postal code the user typed. Everything else came from the record or from somebody's hand and is
 * left exactly as it is: the locality directory is a second opinion, not the authority on where
 * somebody lives, so a contradiction is put to the user (see `describeCity`) instead of being
 * settled behind their back — and saving stays possible whatever the directory says.
 *
 * Its own city it remembers by the value it wrote, not by a guess about where the value came from.
 * That is the point: a reload, a record arriving after the first render or a change from somewhere
 * else can all make a saved city look edited, and none of them may turn somebody else's entry into
 * the automation's to overwrite.
 *
 * And it holds that city only for as long as the record is being edited. Leaving edit mode is what
 * turns an entry into part of the record, whoever or whatever put it there, and from then on it is
 * honoured like any other saved value: the automation starts over, exactly as it does on an address
 * it has just been given.
 */
export function useCityDecision({
    lookup,
    addressId,
    city,
    applyCity,
    enabled,
    isEditing,
}: UseCityDecisionArgs): CityDecisionState {
    const { country, zip, status, localities } = lookup;

    /**
     * The city the automation last put into the record, exactly as it wrote it — and undefined
     * again as soon as the user takes the field in hand, because from then on the entry is theirs
     * whatever put it there first.
     */
    const [cityWritten, setCityWritten] = useState<string | undefined>(undefined);
    const [pendingCity, setPendingCity] = useState<string | undefined>(undefined);
    const [keptCity, setKeptCity] = useState<string | undefined>(undefined);
    const [hasTypedZip, setHasTypedZip] = useState(false);
    const [hasTypedCity, setHasTypedCity] = useState(false);

    /**
     * Set while the user is typing in the city field, where the record is a keystroke behind the
     * screen. Deliberately not a dependency of the effect below — reacting to the blur would write
     * over the entry that was just made — but state all the same, because what the answer makes of
     * the field is rendered, and while the user is in it the answer makes nothing of it at all.
     */
    const [isCityBeingEdited, setIsCityBeingEdited] = useState(false);

    /** What the field shows, or is one render from showing — the truth about it either way. */
    const shownCity = pendingCity ?? city;
    const isCityOurs = shownCity === '' || shownCity === cityWritten;

    // The one reading of what this answer does to the field. The effect below writes it, the notice
    // speaks of it and the cursor waits for it, so none of the three can take a different view of
    // where the field is headed — nor say anything about a city that is a render from being gone.
    //
    // A field already holding one of this answer's localities is settled: the automation wrote it,
    // or the user picked it, and either way the question has been answered. Anything else it may
    // write over becomes the one locality the postal code leaves no choice about, or nothing.
    const isAnswered =
        !enabled || !isCityOurs || !hasTypedZip || isCityBeingEdited || matchesLocality(localities, shownCity);
    const cityWhenSettled = isAnswered || status !== 'ready' ? shownCity : onlyLocality(localities) ?? '';

    /**
     * Read when the answer arrives rather than when it is asked for, so that only a new answer can
     * set the automation off. The city because settling the field writes it, and reacting to one's
     * own write would never end. The permission because it is earned by typing a postal code, and
     * that keystroke must not by itself fill a field the answer before it already spoke about. The
     * callback because the form hands down a new one on every render.
     */
    const latest = useStable({ city, shownCity, applyCity, cityWhenSettled, isAnswered });

    useEffect(() => {
        // Another address, or this one taken out of editing. Either way what is on screen is a
        // record in its own right now, nothing in it is the automation's doing any more, and no
        // question stands unanswered: it starts over, the way it does on a freshly given address.
        setCityWritten(undefined);
        setPendingCity(undefined);
        setKeptCity(undefined);
        setHasTypedZip(false);
        setHasTypedCity(false);
    }, [addressId, isEditing]);

    useEffect(() => {
        // The user answered about the postal code standing then, and this is a different question.
        setKeptCity(undefined);
    }, [country, zip]);

    useEffect(() => {
        const { shownCity: cityInField, applyCity: write, cityWhenSettled: wanted } = latest.current;

        // Everything that could keep the automation off the field, in the one reading the notice
        // and the cursor go by as well: the field is not its own, nobody asked, the user is in it,
        // or the answer has already been settled. Typing a postal code is the only way to ask — no
        // record arriving, reloading or being changed elsewhere can stand in for it.
        if (latest.current.isAnswered) {
            return;
        }

        const settle = (settled: string) => {
            if (settled === cityInField) {
                return;
            }
            setCityWritten(settled);
            setPendingCity(settled);
            write(settled);
        };

        if (zip === '') {
            // The postal code is gone, so what it put in the city goes with it.
            settle('');
            return;
        }

        if (status !== 'ready') {
            // Nothing to go by: still searching, not a postal code yet, an outage on our side, or
            // a postal code the directory knows no place under. None of these says anything about
            // the city, so none of them moves it.
            return;
        }

        // The one locality a postal code leaves no choice about is filled in. Several are a
        // question only the user can answer, and the field is where they answer it: emptied, with
        // the choice in the suggestion list beside it.
        settle(wanted);
    }, [enabled, status, zip, localities, latest]);

    useEffect(() => {
        // The record has caught up with the write, so it is no longer on its way in.
        if (pendingCity === city) {
            setPendingCity(undefined);
        }
    }, [pendingCity, city]);

    const noteZipTyped = useCallback(() => {
        // Only the field calls this, so this is the user asking — and the one thing that ever
        // gives the automation leave to write.
        setHasTypedZip(true);
    }, []);

    const noteLocalityPicked = useCallback((locality: string) => {
        // Picked out of what the postal code offered, so the automation knows where it came from
        // and may move it on with the next postal code. No write of its own: the field commits the
        // pick, and writing it here as well would put the same city into the record twice.
        setCityWritten(locality);
        setPendingCity(locality);
    }, []);

    const acceptLocality = useCallback(
        (locality: string) => {
            // The same step, with the write the field would otherwise have done itself.
            noteLocalityPicked(locality);
            applyCity(locality);
        },
        [applyCity, noteLocalityPicked],
    );

    const keepCity = useCallback(() => {
        // Nothing to write: the city that is asked about is one the automation was keeping off
        // anyway. All this settles is that the question has now been put and answered.
        setKeptCity(city);
    }, [city]);

    const noteCityTyping = useCallback(
        (value: string) => {
            // The field reports on leaving it too, to trim what is there. A report that changes
            // nothing is nobody taking anything in hand, and would otherwise hand the user a city
            // the automation had just filled in or that was picked from the list.
            if (value === latest.current.city) {
                return;
            }
            setIsCityBeingEdited(true);
            setHasTypedCity(true);
            // Typed by hand, so it is the user's from here on, and no longer the automation's to
            // replace — which also closes the gap the two-step write leaves, where the user
            // reaches the field within the render it takes the record to catch up.
            setCityWritten(undefined);
            setPendingCity(undefined);
        },
        [latest],
    );

    const noteCityBlurred = useCallback(() => {
        setIsCityBeingEdited(false);
    }, []);

    return {
        pendingCity,
        isCityOurs,
        cityWhenSettled,
        keptCity,
        hasTypedZip,
        hasEdited: hasTypedZip || hasTypedCity,
        noteZipTyped,
        acceptLocality,
        noteLocalityPicked,
        keepCity,
        noteCityTyping,
        noteCityBlurred,
    };
}
